// The recordings list's Distance / Elevation / Type / More (features, activities) / Tags
// filters — the same set and the same GraphQL argument names as the My Hikes list
// (apps/hikes/Hikes.vue), served by the recording queries' RecordingListFilters on the
// backend. The apps open the Explore filter sheet for the same filters.
//
// Sliders work in the user's units; a range whose upper end sits on the slider's bound
// means "no maximum", exactly as on My Hikes.

/** Hike types a recording can be classified as. No multi-day: a recording is one walk. */
export const RECORDING_ROUTE_TYPES = ['point_to_point', 'loop', 'out_and_back'] as const;

export type RecordingRouteType = (typeof RECORDING_ROUTE_TYPES)[number];

export interface RecordingRouteTypeOption {
  value: RecordingRouteType;
  labelKey: string;
}

// The Type dropdown's choices. Each label key is written out in full so the client
// translation bundle's key scan (lib/i18n_client_bundle.rb) ships it; a key assembled
// at runtime from a template string is invisible to that scan.
const ROUTE_TYPE_OPTIONS_BY_TYPE: Record<RecordingRouteType, RecordingRouteTypeOption> = {
  point_to_point: { value: 'point_to_point', labelKey: 'hikes.point_to_point' },
  loop: { value: 'loop', labelKey: 'hikes.loop' },
  out_and_back: { value: 'out_and_back', labelKey: 'hikes.out_and_back' },
};

export const RECORDING_ROUTE_TYPE_OPTIONS: readonly RecordingRouteTypeOption[] = Object.freeze(
  RECORDING_ROUTE_TYPES.map((type) => ROUTE_TYPE_OPTIONS_BY_TYPE[type]),
);

/** The option for a selected type, or null for "all types" or a type the list doesn't offer. */
export function recordingRouteTypeOption(type: string | null | undefined): RecordingRouteTypeOption | null {
  return RECORDING_ROUTE_TYPE_OPTIONS.find((option) => option.value === type) ?? null;
}

export type Range = [number, number];

export interface RecordingListFilterState {
  distanceRange: Range;
  distanceMaxBound: number;
  elevationRange: Range;
  elevationMaxBound: number;
  routeType: string | null;
  featureTag: string | null;
  activityTag: string | null;
  customTag: string | null;
}

export interface RecordingListUnitConverters {
  metresFromBigUnit(value: number): number;
  metresFromSmallUnit(value: number): number;
}

export interface RecordingListFilterVariables {
  distanceMetersMin: number | null;
  distanceMetersMax: number | null;
  elevationGainMetersMin: number | null;
  elevationGainMetersMax: number | null;
  routeType: string | null;
  featureTags: string[] | null;
  activityTags: string[] | null;
  customTags: string[] | null;
}

export function defaultRecordingListFilterState(
  distanceMaxBound: number,
  elevationMaxBound: number,
): RecordingListFilterState {
  return {
    distanceRange: [0, distanceMaxBound],
    distanceMaxBound,
    elevationRange: [0, elevationMaxBound],
    elevationMaxBound,
    routeType: null,
    featureTag: null,
    activityTag: null,
    customTag: null,
  };
}

export function isRangeFilterActive(range: Range, maxBound: number): boolean {
  return range[0] !== 0 || range[1] !== maxBound;
}

/** Feature and activity tags share the "More" pill, as on My Hikes. */
export function moreFilterState(state: RecordingListFilterState) {
  return {
    featureTag: state.featureTag,
    activityTag: state.activityTag,
    // My Hikes folds its date range and hide-completed into "More"; the recordings list
    // keeps Date as its own pill and has no hide-completed, so both sit at their defaults.
    startDate: null,
    endDate: null,
    hideCompleted: true,
  };
}

export function hasActiveRecordingListFilters(state: RecordingListFilterState): boolean {
  return isRangeFilterActive(state.distanceRange, state.distanceMaxBound) ||
    isRangeFilterActive(state.elevationRange, state.elevationMaxBound) ||
    Boolean(state.routeType || state.featureTag || state.activityTag || state.customTag);
}

/**
 * The query variables for the current filters. Anything at its default is null, which
 * the server treats as "not filtering" — the same request the list made before these
 * filters existed.
 */
export function recordingListFilterVariables(
  state: RecordingListFilterState,
  units: RecordingListUnitConverters,
): RecordingListFilterVariables {
  const [distanceMin, distanceMax] = state.distanceRange;
  const [elevationMin, elevationMax] = state.elevationRange;
  const list = (value: string | null) => (value ? [value] : null);

  return {
    distanceMetersMin: distanceMin > 0 ? Math.round(units.metresFromBigUnit(distanceMin)) : null,
    distanceMetersMax: distanceMax >= state.distanceMaxBound ? null : Math.round(units.metresFromBigUnit(distanceMax)),
    elevationGainMetersMin: elevationMin > 0 ? Math.round(units.metresFromSmallUnit(elevationMin)) : null,
    elevationGainMetersMax: elevationMax >= state.elevationMaxBound ? null : Math.round(units.metresFromSmallUnit(elevationMax)),
    routeType: state.routeType || null,
    featureTags: list(state.featureTag),
    activityTags: list(state.activityTag),
    customTags: list(state.customTag),
  };
}
